import { defineComponent as _defineComponent } from 'vue'
import SearchFilterChip from "@/components/vuetify-wrappers/SearchFilterChip.vue";
import { useSearchStore } from "@/pinia/search";
import { computed, ref, watch } from "vue";
import {
  LookupTablesKeys,
  NestedLookupTableItem,
  NestedLookupTablesKeys,
  SearchQueryUpdatePayload,
} from "@/types";
import { LogService, SearchService } from "@/services";
import { useRoute, useRouter } from "vue-router/composables";
import { useAppStore } from "@/pinia/app";

interface SearchFilterChipObject {
  value: string | string[] | NestedLookupTableItem[];
  label: string;
  clearValue: any;
  key: string;
}

// list of keys to ignore in parsing the query
interface SpecialKey {
  show: () => boolean;
  valueGetter: () => string | string[];
  label?: string;
}

export default /*#__PURE__*/_defineComponent({
  __name: 'SearchChipsContainer',
  setup(__props) {

const router = useRouter();
const route = useRoute();

const appStore = useAppStore();
const appStoreAgencies = appStore.getNestedLookupTable(
  NestedLookupTablesKeys.AGENCIES
);
const appStoreAgenciesFlat = appStore.getLookupTable(
  LookupTablesKeys.AGENCIES_FLAT
);

const searchStore = useSearchStore();

// grab the current query
const currentQuery = ref(SearchService.readSearchQueriesFromRoute());
watch(route, () => {
  currentQuery.value = SearchService.readSearchQueriesFromRoute();
});

const keysToIgnore = [
  "birthDateOperator",
  "hidePayWalledProfiles",
  "jobtitle2_option",
  "offset",
  "order",
  "page",
  "pageSize",
  "sigOperator",
  "sigRating",
  "view",
];

const specialKeys: { [key: string]: SpecialKey } = {
  age: {
    show: () => currentQuery.value.age !== null,
    valueGetter: () => {
      const operator = currentQuery.value.birthDateOperator.id;
      if (currentQuery.value.age.id && operator) {
        return `${
          operator === "lt" ? "under" : operator === "gte" ? "over" : "is"
        } ${currentQuery.value.age.name}`;
      }
      return "";
    },
  },
  birthYear: {
    label: "Birth Year",
    show: () => currentQuery.value.birthYear !== null,
    valueGetter: () => {
      return `${currentQuery.value.birthDateOperator.name} ${currentQuery.value.birthYear.name}`;
    },
  },
  sig: {
    show: () => currentQuery.value.sig !== null,
    valueGetter: () => {
      return `${currentQuery.value.sig?.name} Rated ${currentQuery.value.sigOperator.name} ${currentQuery.value.sigRating.name}`;
    },
    label: "Special Interest Group Rating",
  },
  jobtitle2: {
    show: () => !!currentQuery.value.jobtitle2,
    label: "Job Title",
    valueGetter: () => {
      return `${currentQuery.value.jobtitle2_option} ${currentQuery.value.jobtitle2}`;
    },
  },
  agency: {
    show: () => !!currentQuery.value.agency,
    label: "Organization",
    valueGetter: () => {
      const selectedAgencies: string[] = currentQuery.value.agency;
      return selectedAgencies.map((a) => {
        const agency = appStoreAgencies.find((ag) => ag.id === a);
        return agency?.name ?? "";
      });
    },
  },
  agencies: {
    show: () => currentQuery.value.agencies.length > 0,
    label: "Federal Agencies",
    valueGetter: () => {
      return currentQuery.value.agencies.map((a: string) => {
        const agency = appStoreAgenciesFlat.find((ag) => ag.id === a);
        return agency?.name ?? "";
      });
    },
  },
  appointment: {
    show: () => currentQuery.value.appointment.length > 0,
    label: "Appointment",
    valueGetter: () => {
      return currentQuery.value.appointment.map((a: string) => {
        const appointment = appStore
          .getLookupTable(LookupTablesKeys.APPOINTMENTS)
          .find((ap) => ap.id === a);
        return appointment?.name ?? "";
      });
    },
  },
};

// get the keys into a list so it is easier to iterate over and filter out any keys that don't have a value set
const searchKeys = computed(() => {
  const result = [];
  for (const key in currentQuery.value) {
    if (!keysToIgnore.includes(key)) {
      const value = currentQuery.value[key as keyof typeof currentQuery.value];
      if (Array.isArray(value)) {
        value.length > 0 &&
          result.push({
            key,
            value: currentQuery.value[key as keyof typeof currentQuery.value],
          });
      } else if (typeof value === "object" && value !== null) {
        !!value.id &&
          result.push({
            key,
            value: currentQuery.value[key as keyof typeof currentQuery.value],
          });
      } else if (value) {
        result.push({
          key,
          value: currentQuery.value[key as keyof typeof currentQuery.value],
        });
      }
    }
  }
  return result;
});

const parseKeyToLabel = (key: string): string => {
  if (key.includes("_")) {
    return key
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const getClearValue = (key: keyof typeof currentQuery.value) => {
  const defaultQuery = searchStore.getDefaultSearchQuery();
  return defaultQuery[key];
};

const parsePotentialObject = (
  object: { key: string; value: any } | null
): any => {
  if (object === null) {
    return {
      value: "",
      clearValue: "",
    };
  }
  const value = object.value;
  if (typeof value === "string") {
    return value;
  }
  if (Array.isArray(value)) {
    return value;
  }
  return value.name;
};

const searchFilterChips = computed(() => {
  const result: SearchFilterChipObject[] = [];
  for (const key in searchKeys.value) {
    const object = searchKeys.value[key];
    const value = object.value;
    const k = object.key;
    if (k in specialKeys) {
      const specialKey = specialKeys[k as keyof typeof specialKeys];
      if (specialKey.show()) {
        result.push({
          value: specialKey.valueGetter(),
          label: specialKey.label || parseKeyToLabel(k),
          clearValue: getClearValue(k as keyof typeof currentQuery),
          key: k,
        });
      }
    } else {
      if (Array.isArray(value)) {
        const parsed = parsePotentialObject(object);
        result.push({
          value: parsed,
          label: parseKeyToLabel(k),
          clearValue: getClearValue(k as keyof typeof currentQuery),
          key: k,
        });
        // else if value is an object
      } else if (typeof value === "object") {
        const parsed = parsePotentialObject(object);
        result.push({
          value: parsed,
          label: parseKeyToLabel(k),
          clearValue: getClearValue(k as keyof typeof currentQuery),
          key: k,
        });
      } else if (value) {
        result.push({
          value: `${value}`,
          label: parseKeyToLabel(k),
          clearValue: getClearValue(k as keyof typeof currentQuery),
          key: k,
        });
      }
    }
  }
  return result;
});

const updateLastSearch = (...payloads: SearchQueryUpdatePayload[]): void => {
  for (const payload of payloads) {
    LogService.info(
      `SearchResults.updateLastSearch: payload: '${payload.field}', value: '${payload.value}'`
    );
    searchStore.setLastSearchQueryField(payload);
  }
  // since this is called only on deleting a filter, we check to see if the non-reactive (until route change)
  // searchFilterChips has one left, if so that means it will be empty after the route change, so we reset the search
  if (searchFilterChips.value.length > 1) {
    SearchService.search(router, true);
  } else {
    searchStore.resetSearch();
    router.replace({ query: {} }).catch(() => null);
    searchStore.$patch({ searchFormIsHidden: false });
  }
};

return { __sfc: true,router, route, appStore, appStoreAgencies, appStoreAgenciesFlat, searchStore, currentQuery, keysToIgnore, specialKeys, searchKeys, parseKeyToLabel, getClearValue, parsePotentialObject, searchFilterChips, updateLastSearch, SearchFilterChip }
}

})